import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { useDebounce } from 'use-debounce';

import {
  CUT_DRY_IMPORT_PRODUCTS,
  GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
  GLOBAL_VENDORS_FOR_DROPDOWN,
} from 'lib/networking/endpoints';
import EntityType from 'lib/enums/EntityType';
import PaginationComponent from 'components/shared/PaginationComponent';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import useGet from 'lib/hooks/useGet';

const CutDryImportTrackingProductsTab = ({ runId, importRun, getActionBadge }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [productFilters, setProductFilters] = useState({
    manufacturer_id: null,
    vendor_id: null,
  });
  const [skuSearch, setSkuSearch] = useState('');
  const [debouncedSkuSearch] = useDebounce(skuSearch, 500);

  // Fetch global vendors data
  const { data: { data: globalVendors } = {} } = useGet(GLOBAL_VENDORS_FOR_DROPDOWN);

  // Fetch products data
  const {
    data: { products = [], total_count: productsTotal = 0 } = {},
    loading: productsLoading,
    error: productsError,
    refetch: refetchProducts,
  } = useGet(CUT_DRY_IMPORT_PRODUCTS, {
    cut_dry_import_run_id: runId,
    offset: (currentPage - 1) * pageSize,
    limit: pageSize,
    ...(productFilters.manufacturer_id && {
      manufacturer_id: productFilters.manufacturer_id.value,
    }),
    ...(productFilters.vendor_id && { vendor_id: productFilters.vendor_id.value }),
    ...(debouncedSkuSearch && { sku: debouncedSkuSearch }),
  });

  // Trigger refetch when dependencies change
  useEffect(() => {
    if (refetchProducts) {
      refetchProducts();
    }
  }, [currentPage, pageSize, productFilters, debouncedSkuSearch, refetchProducts]);

  return (
    <>
      <Row className="mb-3">
        {importRun.import_type === EntityType.MANUFACTURER && (
          <Col md={3}>
            <Form.Group>
              <Form.Label>Select Manufacturer</Form.Label>
              <SearchableScrollableSelect
                onChange={option => {
                  setProductFilters(prev => ({
                    ...prev,
                    manufacturer_id: option,
                    vendor_id: null,
                  }));
                  setCurrentPage(1);
                }}
                fetchUrl={GLOBAL_MANUFACTURERS_FOR_DROPDOWN}
                optionMapper={manufacturer => ({
                  label: manufacturer.name,
                  value: manufacturer.id,
                })}
                initOption={productFilters.manufacturer_id}
                autoFocus={false}
                placeholder="Search manufacturer..."
                disabled={productFilters.vendor_id}
              />
            </Form.Group>
          </Col>
        )}
        {importRun.import_type === EntityType.DISTRIBUTOR && (
          <Col md={3}>
            <Form.Group>
              <Form.Label>Select Distributor</Form.Label>
              <Select
                value={productFilters.vendor_id}
                options={globalVendors?.map(vendor => ({
                  value: vendor.id,
                  label: vendor.name,
                }))}
                onChange={e => {
                  setProductFilters(prev => ({
                    ...prev,
                    vendor_id: e,
                    manufacturer_id: null,
                  }));
                  setCurrentPage(1);
                }}
                placeholder="Search distributor..."
                isDisabled={productFilters.manufacturer_id}
                isSearchable
                isClearable
              />
            </Form.Group>
          </Col>
        )}
        <Col md={3}>
          <Form.Group>
            <Form.Label>Search by SKU</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter SKU"
              value={skuSearch}
              onChange={e => setSkuSearch(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>&nbsp;</Form.Label>
            <div>
              <Button
                variant="primary"
                onClick={() => {
                  setProductFilters({ manufacturer_id: null, vendor_id: null });
                  setSkuSearch('');
                  setCurrentPage(1);
                }}
              >
                Clear Filters
              </Button>
            </div>
          </Form.Group>
        </Col>
      </Row>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5>
            {productsTotal > 0 ? `${productsTotal} Processed Products` : 'Processed Products'}
          </h5>
          <PaginationComponent
            totalItems={productsTotal}
            itemsPerPage={pageSize}
            setItemsPerPage={setPageSize}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            disabled={productsLoading}
          />
        </Card.Header>
        <Card.Body>
          {productsLoading ? (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          ) : productsError ? (
            <div className="text-center text-danger">
              Error loading products: {productsError.message}
            </div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Product Name</th>
                  <th>SKU</th>
                  <th>Entity</th>
                  <th>View Product</th>
                </tr>
              </thead>
              <tbody>
                {products.length > 0 ? (
                  products.map(product => {
                    let productLink = '';
                    if (
                      importRun.import_type === EntityType.MANUFACTURER &&
                      product.global_product_manufacturer
                    ) {
                      const manufacturerProduct = product.global_product_manufacturer;
                      const globalProd = manufacturerProduct.global_product_id;
                      const prodId = manufacturerProduct.id;
                      const type = importRun.import_type.toLowerCase();
                      productLink = `/global-product-detail/${globalProd}/${prodId}/${type}`;
                    } else if (
                      importRun.import_type === EntityType.DISTRIBUTOR &&
                      product.global_product_vendor
                    ) {
                      const vendorProduct = product.global_product_vendor;
                      const globalProd = vendorProduct.global_product_id;
                      const prodId = vendorProduct.id;
                      const type = importRun.import_type.toLowerCase();
                      productLink = `/global-product-detail/${globalProd}/${prodId}/${type}`;
                    }

                    return (
                      <tr key={product.id}>
                        <td>{getActionBadge(product.action_taken)}</td>
                        {importRun.import_type === EntityType.MANUFACTURER &&
                          product.global_product_manufacturer && (
                            <>
                              <td>{product.global_product_manufacturer.name}</td>
                              <td>{product.global_product_manufacturer.manufacturer_sku}</td>
                              <td>
                                {product.global_product_manufacturer.global_manufacturer?.name}
                              </td>
                              <td>
                                {productLink && (
                                  <Link to={productLink} target="_blank" rel="noopener noreferrer">
                                    View Product
                                  </Link>
                                )}
                              </td>
                            </>
                          )}
                        {importRun.import_type === EntityType.DISTRIBUTOR &&
                          product.global_product_vendor && (
                            <>
                              <td>{product.global_product_vendor.name}</td>
                              <td>{product.global_product_vendor.vendor_sku}</td>
                              <td>{product.global_product_vendor.global_vendor?.name}</td>
                              <td>
                                {productLink && (
                                  <Link to={productLink} target="_blank" rel="noopener noreferrer">
                                    View Product
                                  </Link>
                                )}
                              </td>
                            </>
                          )}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">
                      No products found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

CutDryImportTrackingProductsTab.propTypes = {
  runId: PropTypes.string.isRequired,
  importRun: PropTypes.object.isRequired,
  getActionBadge: PropTypes.func.isRequired,
};

export default CutDryImportTrackingProductsTab;
